import { IIcon, IIndexable, ISaveForLater, ISaveForLaterItem } from '../interfaces';
import { getRandomInt } from './math-provider';
import { blobToBase64, svgToPng } from './converters/file-type-converter';

const SEARCH_SKIP_REGEX = new RegExp('[^a-zA-Z0-9]+', 'ig');

/**
 * icons key ---> tags list
 * this map will never change,
 * and it will be initialized only once when the search popup
 * will be opened for the first time;
 */
let searchMap = new Map<string, string[]>();

export const ICONS_SIZES = [16, 32, 48, 96];

export const getRandomIcon = async (iconsData: IIndexable, category: string) : Promise<IIcon> => {

    let keys: string[] = [];

    if(category){
        const temp = Object.keys(iconsData);
        for(let i=0; i<temp.length; i++){
            const tempKey = temp[i];
            const item = iconsData[tempKey];
            if(item.category === category){
                keys.push(tempKey);
            }
        }
    }
    else{
        keys = Object.keys(iconsData);
    }

    const randomIndex = getRandomInt(0, keys.length - 1);
    const iconKey = keys[randomIndex];
    const icon = iconsData[iconKey];

    const url = `tabler-icons/icons/${ iconKey }.svg`;
    const response = await fetch(url);
    const svg = await response.text()

    return {
        svg,
        iconKey,
        category: icon.category,
    }
};

export const getIconByKey = async (iconsData: IIndexable, iconKey: string) : Promise<IIcon|null> => {
    if(!iconsData[iconKey]) return null;

    const icon = iconsData[iconKey];

    const url = `tabler-icons/icons/${ iconKey }.svg`;
    const response = await fetch(url);
    const svg = await response.text()

    return {
        svg,
        iconKey,
        category: icon.category,
    }
};

export const getSaveForLaterIcons = async (iconsData: IIndexable, savedForLaterIcons: ISaveForLater[]) : Promise<ISaveForLaterItem[]> => {
    if(!iconsData || !savedForLaterIcons) return [];

    const result: ISaveForLaterItem[] = [];

    for(let saveForLaterItem of savedForLaterIcons){
        const icon = await getIconByKey(iconsData, saveForLaterItem.iconKey);
        if(!icon) continue;

        result.push({
            saveForLaterItem,
            icon
        });
    }

    return result;
};

/**
 * update favicon on the current website in the real time for preview
 */
export const updateFaviconDynamically = async ($svg: SVGSVGElement, size: number) => {

    const svgToPngResult = await svgToPng($svg);
    if(!svgToPngResult || !svgToPngResult.blob) return;

    const pngBase64 = await blobToBase64(svgToPngResult.blob) as string;

    const updateLink = (id: string, base64: string) => {
        const $link = document.getElementById(id) as HTMLLinkElement;
        if(!$link) return;
        $link.setAttribute('href', base64);
    };

    // 'favicon-....png'
    updateLink(`favicon-${ size }`, pngBase64);
};

export const convertSearchObjectToMap = (iconsData: IIndexable) => {

    searchMap = new Map<string, string[]>();

    const keys = Object.keys(iconsData);
    for(let i=0; i<keys.length; i++){
        const iconKey = keys[i];
        const iconData = iconsData[iconKey] as IIcon;
        if(!iconData.tags) continue;

        searchMap.set(iconKey, iconData.tags.map(tag => tag.replace(SEARCH_SKIP_REGEX, '').toLowerCase().trim()));
    }
};

export const searchIcons = (iconsData: IIndexable, term: string) => {

    // create search map for the first time (if needed);
    // this map will never change
    if(!searchMap || searchMap.size === 0){
        convertSearchObjectToMap(iconsData);
    }

    const formattedTerm = term.replace(SEARCH_SKIP_REGEX, '').toLowerCase().trim();
    const iconKeys: Set<string> = new Set();

    if(formattedTerm !== ''){
        for(let [iconKey, tags] of searchMap){

            for(let i=0; i<tags.length; i++){
                const tag = tags[i];
                if(tag.indexOf(formattedTerm) !== -1){
                    iconKeys.add(iconKey);
                    break;
                }
            }
        }
    }

    return Array.from(iconKeys);
};